/**
 * MKS-447 (part 6) — the Linear ↔ Monksheets reconciliation report.
 *
 * Mirrors `backend/src/api/routes/linearSyncRoutes.ts`
 * (`POST /api/admin/linear/issues/reconcile`). Admin-only.
 *
 * The request body is a batch of Linear issue payloads, in exactly the shape
 * n8n posts to the sync endpoint. The endpoint writes nothing.
 */
import { apiPost } from './client';

/** Keep in step with `MAX_RECONCILE_ISSUES` in the backend schema. */
export const MAX_RECONCILE_ISSUES = 50;

export type LinearFieldStatus =
  | 'matched'
  | 'differing'
  | 'missing_in_monksheets'
  | 'monksheets_only';

export interface LinearFieldComparison {
  field: string;
  status: LinearFieldStatus;
  linear: string | null;
  monksheets: string | null;
  detail?: string;
}

export interface LinearIssueReconciliation {
  linearIssueId: string;
  identifier: string;
  teamKey: string;
  title: string;
  taskId: number | null;
  projectId: number | null;
  matchedBy: string | null;
  missingInMonksheets: boolean;
  status: LinearFieldStatus;
  fields: LinearFieldComparison[];
  /** Set instead of `fields` when the issue could not be compared at all. */
  error?: string;
}

export interface LinearReconciliationReport {
  issues: LinearIssueReconciliation[];
  counts: {
    total: number;
    matched: number;
    differing: number;
    missingInMonksheets: number;
    monksheetsOnly: number;
    failed: number;
  };
  generatedAt: string;
}

/**
 * The issue payloads are passed through untouched. They come from Linear via
 * n8n and the server validates every bound, so the SPA deliberately does not
 * restate the shape — it would be a second schema to keep in lockstep, and the
 * one that cannot reject anything.
 */
export async function reconcileLinearIssues(
  issues: unknown[],
): Promise<LinearReconciliationReport> {
  const res = await apiPost<{ data: LinearReconciliationReport }>(
    '/admin/linear/issues/reconcile',
    { issues },
  );
  return res.data;
}
